import { Box, Flex, BoxProps, Image } from '@everlywell/ui-kit';
import SectionContainer from 'components/PageSections/components/SectionContainer';
import VideoComponent, { VideoComponentProps } from 'components/VideoComponent';
import { GatsbyImage } from 'gatsby-plugin-image';
import React, { useEffect, useState } from 'react';
import { ImageType } from 'utils/types';

import BenefitCardDescription, {
  BenefitCardDescriptionProps,
} from './BenefitCardDescription';

export type BenefitsCardsProps = {
  mobileImage?: ImageType | null;
  desktopImage?: ImageType | null;
  mobileVideo?: VideoComponentProps | null;
  desktopVideo?: VideoComponentProps | null;
  benefitsCardDescription?: BenefitCardDescriptionProps;
};

/**
 * BenefitsCards component
 * A component that displays a section with a benefit card description and a media element (video or image).
 * The media element changes based on screen size (mobile/desktop).
 * @param {ImageType | null} [props.mobileImage] Image to display on mobile screens
 * @param {ImageType | null} [props.desktopImage] Image to display on desktop screens
 * @param {VideoComponentProps | null} [props.mobileVideo] Video to display on mobile screens. If provided, it will ignore the mobileImage prop.
 * @param {VideoComponentProps | null} [props.desktopVideo] Video to display on desktop screens. If provided, it will ignore the desktopImage prop.
 * @param {BenefitCardDescriptionProps} [props.benefitsCardDescription] Props for the BenefitCardDescription component
 * @returns {JSX.Element} BenefitsCards component
 */
export default function BenefitsCards({
  mobileImage,
  desktopImage,
  mobileVideo,
  desktopVideo,
  benefitsCardDescription,
}: BenefitsCardsProps) {
  // State to track if page is loaded to play video
  const [shouldPlayVideo, setShouldPlayVideo] = useState(false);

  // Effect to set shouldPlayVideo to true after component mounts
  useEffect(() => {
    setShouldPlayVideo(true);
  }, []);

  const displayMobileConfig: BoxProps['display'] = ['flex', null, null, 'none'];
  const displayDesktopConfig: BoxProps['display'] = [
    'none',
    null,
    null,
    'flex',
  ];

  return (
    <SectionContainer
      size={['sm', null, null, 'xl']}
      paddingX={[4, null, null, 12]}
      paddingY={[4, null, null, 0]}
      display="flex"
      flexDirection={['column', null, null, 'row-reverse']}
      gap={[2, null, null, 4]}
    >
      <Flex
        width={['100%', null, null, '440px']}
        minHeight={['auto', null, null, '640px']}
      >
        <BenefitCardDescription {...benefitsCardDescription} />
      </Flex>
      {/* Video or Image Card */}
      <Flex flex="1" borderRadius="3xl" overflow="hidden">
        {/* Mobile Video or Image - Check for video first   */}
        {mobileVideo ? (
          <Box
            display={displayMobileConfig}
            height={['360px', 'auto']}
            width="100%"
          >
            <VideoComponent
              {...mobileVideo}
              loop={true}
              objectFit="cover"
              objectPosition="left"
              height="auto"
              autoPlay={shouldPlayVideo}
              {...(mobileImage && {
                poster: mobileImage?.gatsbyImageData?.images?.fallback?.src,
              })}
            />
          </Box>
        ) : mobileImage ? (
          <Box display={displayMobileConfig}>
            <Image image={mobileImage.gatsbyImageData} as={GatsbyImage} />
          </Box>
        ) : null}

        {/* Desktop Video or Image - Check for video first */}
        {desktopVideo ? (
          <Box display={displayDesktopConfig} width="100%">
            <VideoComponent
              {...desktopVideo}
              loop={true}
              objectFit="cover"
              objectPosition="left"
              height="auto"
              autoPlay={shouldPlayVideo}
              {...(desktopImage && {
                poster: desktopImage?.gatsbyImageData?.images?.fallback?.src,
              })}
            />
          </Box>
        ) : desktopImage ? (
          <Box display={displayDesktopConfig}>
            <Image image={desktopImage.gatsbyImageData} as={GatsbyImage} />
          </Box>
        ) : null}
      </Flex>
    </SectionContainer>
  );
}
